import { Component, inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { DatePipe } from '@angular/common';
import { PanelDirective } from '../../directives/panel.directive';
import { TranslatePipe } from '../../filters/translate.pipe';
import Session from '../../utils/session.service';
import { LoaderOverlayComponent } from '../../components/loader-overlay/loader-overlay.component';
import RestService from '../../services/core-rest.service';
import AngularRoutingHelperService from '../../services/angular-routing-helper.service';
import AngularHelperService from "../../services/angular-helper.service";
import AngularFilterHelperService from "../../services/angular-filter-helper.service";
import {Router} from "@angular/router";

@Component({
	standalone: true,
	selector: 'assigned-training-programs-list',
	templateUrl: './assigned-training-programs.component.html',
	imports: [CommonModule, DatePipe, PanelDirective, TranslatePipe, LoaderOverlayComponent]
})
export default class AssignedTrainingProgramsListComponent implements OnInit {
	private session: Session = inject(Session);
	private restService: RestService = inject(RestService);
	private angularRoutingHelperService: AngularRoutingHelperService = inject(AngularRoutingHelperService);
	private angularHelperService: AngularHelperService = inject(AngularHelperService);
	private angularFilterHelperService: AngularFilterHelperService = inject(AngularFilterHelperService);
	private router: Router = inject(Router);

	vm: any = undefined;

	constructor() {
		this.vm = this;
	}

	ngOnInit() {
		this.vm.loading = true;
		this.vm.error = false;
		this.vm.programs = [];
		this.vm.filteredPrograms = [];
		this.vm.assignedProgress = [];
		this.vm.user = (this.session.getSession() || {}).user;
		this.vm.isBusinessUser = this.vm.user.businessUserType === 'BUSINESS_CLIENT' || this.vm.user.businessUserType === 'SERVICE_PROVIDER' || this.vm.user.businessUserType === 'SERVICE_OWNER';
		this.vm.selectedFilter = 'all';

		this.vm.openProgram = this.openProgram.bind(this);
		this.vm.openModule = this.openModule.bind(this);
		this.vm.setFilter = this.setFilter.bind(this);
		this.vm.getProgramStatusKey = this.getProgramStatusKey.bind(this);
		this.vm.getProgramStatusClass = this.getProgramStatusClass.bind(this);
		this.vm.getProgramStatusLabel = this.getProgramStatusLabel.bind(this);
		this.vm.getActionLabel = this.getActionLabel.bind(this);
		this.vm.getIconClass = this.getIconClass.bind(this);
		this.angularHelperService.createTimer(() => {
			if(!this.vm.isBusinessUser) {
				this.restService.getTrainingProgramsForLearner()
					.then(async (response) => {
						const programs = response || [];
						const enrichedPrograms = await Promise.all((programs || []).map(async (program) => {
							const params = program?.versionCode ? { programVersionCode: program.versionCode } : {};
							const summary = await this.restService.getTrainingLearnerSummary(params).catch(() => null);
							return this.decorateProgram(program, summary);
						}));
						this.vm.programs = enrichedPrograms;
						this.vm.filteredPrograms = this.vm.programs;
						this.vm.loading = false;
					})
					.catch((error) => {
						console.log(error);
						this.vm.error = true;
						this.vm.loading = false;
					});
			}
		}, 200);
	}

	getProgramStatus(program) {
		return program?.statusKey || program?.status || 'assigned';
	}

	decorateProgram(program, summary) {
		const totalModules = summary?.totalModules ?? program?.totalModules ?? 0;
		const completedModules = summary?.completedModules ?? 0;
		const progressPercent = totalModules > 0 ? Math.round((completedModules / totalModules) * 100) : 0;
		const statusKey = this.getProgramStatusKey({
			...program,
			status: completedModules >= totalModules && totalModules > 0
				? 'completed'
				: completedModules > 0
					? 'in_progress'
					: (program?.status || 'assigned')
		});
		const circumference = 144.5;
		return {
			...program,
			summary: summary || {},
			statusKey,
			totalModulesDisplay: totalModules,
			completedModulesDisplay: completedModules,
			progressPercent,
			progressText: totalModules > 0 ? completedModules + '/' + totalModules : '0/0',
			progressStrokeOffset: circumference - (circumference * progressPercent / 100),
			startedDisplay: this.getStartedDisplay(program),
			dueDisplay: this.getDueDisplay(program),
			completedDisplay: this.getCompletedDisplay(program),
			nextModuleNumber: this.getNextModuleNumber(completedModules, totalModules)
		};
	}

	getProgramStatusKey(program) {
		const status = (this.getProgramStatus(program) || program?.statusKey || '').toString().toLowerCase().replace(/_/g, '-');
		if (status.indexOf('progress') !== -1) return 'in-progress';
		if (status.indexOf('complete') !== -1 || status.indexOf('pass') !== -1) return 'completed';
		return 'assigned';
	}

	getProgramStatusClass(program) {
		return 'assigned-training-program__status--' + this.getProgramStatusKey(program);
	}

	getIconClass(program) {
		return 'assigned-training-program__icon--' + this.getProgramStatusKey(program);
	}

	getProgramStatusLabel(program) {
		const key = this.getProgramStatusKey(program);
		if (key === 'in-progress') return this.angularFilterHelperService.translate('Training.Status.In_Progress');
		if (key === 'completed') return this.angularFilterHelperService.translate('Training.Status.Completed');
		return this.angularFilterHelperService.translate('Training.Status.Assigned');
	}

	getActionLabel(program) {
		return this.getProgramStatusKey(program) === 'completed'
			? this.angularFilterHelperService.translate('Training.Assigned.Review_Program')
			: this.angularFilterHelperService.translate('Training.Assigned.Continue_Module').replace('%s', program?.nextModuleNumber || 1);
	}

	setFilter(filter) {
		this.vm.selectedFilter = filter;
		if (filter === 'all') {
			this.vm.filteredPrograms = this.vm.programs;
			return;
		}
		this.vm.filteredPrograms = (this.vm.programs || []).filter((program) => this.getProgramStatusKey(program) === filter);
	}

	getStartedDisplay(program) {
		const startedAt = program?.startedAt;
		return startedAt ? this.angularFilterHelperService.formatDate(startedAt, 'dd.MM.yyyy') : null;
	}

	getDueDisplay(program) {
		const baseDate = program?.publishedAt || program?.startedAt;
		const weeks = parseInt(program?.completionPeriod, 10);
		if (!baseDate || isNaN(weeks)) return null;
		const dueDate = new Date(baseDate);
		dueDate.setDate(dueDate.getDate() + (weeks * 7));
		return this.angularFilterHelperService.formatDate(dueDate, 'dd.MM.yyyy');
	}

	getCompletedDisplay(program) {
		if (this.getProgramStatusKey(program) !== 'completed') return null;
		return this.getDueDisplay(program);
	}

	getNextModuleNumber(completedModules, totalModules) {
		if (!totalModules || completedModules >= totalModules) return totalModules || 1;
		return completedModules + 1;
	}

	openProgram(program?) {
		const queryParams = program?.versionCode ? { programVersionCode: program.versionCode } : undefined;
		this.angularRoutingHelperService.setPath('/my-training/overview', false, false, queryParams);
	}

	pickCurrentModule(modules) {
		if (!modules || !modules.length) return;
		const completedCount = modules.filter((m) => m.progress && m.progress.status === 'passed').length;
		if (!this.vm.completedModules) {
			this.vm.completedModules = completedCount;
		}
		if (!this.vm.totalModules) {
			this.vm.totalModules = modules.length;
		}
		const firstPending = modules.find((m) => !m.progress || m.progress.status !== 'passed');
		const current = firstPending || modules[modules.length - 1];
		this.vm.currentModuleTitle = current?.module?.title;
		this.vm.currentModuleId = current?.module?.id;
		this.vm.currentModuleKey = current?.module?.moduleKey;
	}

	async openModule(program?) {
		const queryParams = program?.versionCode ? { programVersionCode: program?.versionCode } : undefined;
		if (this.getProgramStatusKey(program) === 'completed') {
			this.router.navigate(['/my-training', 'certificate'], { queryParams });
			return;
		}

		const modules = await this.restService.getTrainingLearnerModules(queryParams).catch(() => []);
		if (!modules || !modules.length) {
			this.openProgram(program);
			return;
		}

		const firstPending = modules.find((m) => !m.progress || m.progress.status !== 'passed');
		const current = firstPending || modules[modules.length - 1];
		const currentModuleId = current?.module?.id;
		if (currentModuleId) {
			this.router.navigate(['/my-training', 'module', currentModuleId], { queryParams });
			return;
		}

		this.openProgram(program);
	}
}
